import { useEffect, useState } from 'react'

const SECOND_MS = 1000
const MINUTE_MS = 60 * SECOND_MS
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

type ClockParts = { day: string; msSinceMidnight: number }

/** Часы в нужном поясе; без пояса — по часам браузера. */
function clockOf(timeZoneId: string | undefined, at: Date): ClockParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZoneId,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  const msSinceMidnight = Number(value('hour')) * HOUR_MS + Number(value('minute')) * MINUTE_MS
    + Number(value('second')) * SECOND_MS + at.getMilliseconds()
  return { day: `${value('year')}-${value('month')}-${value('day')}`, msSinceMidnight }
}

/**
 * Сколько реальных мс до ближайшей полуночи в поясе. Первая оценка считает сутки 24-часовыми; в день перевода часов
 * (сутки 23 или 25 ч) она промахивается на час, поэтому по часам в оценённый момент делается одна поправка.
 */
function msUntilMidnight(timeZoneId: string | undefined, at: Date): number {
  const now = clockOf(timeZoneId, at)
  const guess = DAY_MS - now.msSinceMidnight
  const there = clockOf(timeZoneId, new Date(at.getTime() + guess))
  return there.day === now.day ? guess + DAY_MS - there.msSinceMidnight : guess - there.msSinceMidnight
}

/**
 * Календарный день `ГГГГ-ММ-ДД` по часам браузера или по заданному поясу. Сам сменяется в полночь:
 * один таймер до ближайшей полуночи, без частого опроса. Время читается только в инициализаторе
 * состояния и в таймере, а не в теле рендера, чтобы значение не «застывало» (React Compiler).
 * При смене пояса день пересчитывается сразу после коммита.
 */
export function useCurrentDay(timeZoneId?: string): string {
  const [state, setState] = useState(() => ({ timeZoneId, day: clockOf(timeZoneId, new Date()).day }))

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const tick = () => {
      const at = new Date()
      const now = clockOf(timeZoneId, at)
      setState((previous) => previous.timeZoneId === timeZoneId && previous.day === now.day
        ? previous
        : { timeZoneId, day: now.day })
      timer = setTimeout(tick, Math.max(msUntilMidnight(timeZoneId, at), SECOND_MS))
    }
    tick()
    return () => clearTimeout(timer)
  }, [timeZoneId])

  return state.day
}
